import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { browserDeviceId } from '../lib/deviceId.js';
import { markJoinedAccount } from '../lib/joinedAccounts.js';
import { seedProfileFromProvider } from '../lib/settingsStore.js';

const AuthContext = createContext(null);
const SESSION_KEY = 'demandjet.session';

function routeRole(role) {
  return String(role || 'CLIENT').toUpperCase();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      try {
        setUser(JSON.parse(raw));
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
    }
    setLoading(false);
  }, []);

  function persistSession(session, remember = true) {
    if (remember) {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(SESSION_KEY);
    }
    setUser(session);
    return session;
  }

  async function signup({
    email,
    password,
    displayName,
    role,
    licensedStates,
    remember = true,
    acceptedTerms = false,
    termsVersion,
    privacyVersion,
  }) {
    const data = await api.post('/server/auth_handler/signup', {
      email,
      password,
      displayName,
      role: routeRole(role),
      licensedStates,
      acceptedTerms,
      termsVersion,
      privacyVersion,
    });
    const session = persistSession(data.session, remember);
    markJoinedAccount(session.email, { displayName: session.displayName || displayName });
    return session;
  }

  function finishLogin(data, remember, fallbackName = '') {
    if (data?.mfaRequired) {
      return {
        mfaRequired: true,
        challenge: data.challenge,
        method: data.method,
        hint: data.hint || '',
        email: data.email || '',
      };
    }
    if (!data?.session?.token) throw new Error('Unable to sign in');
    const session = persistSession(data.session, remember);
    markJoinedAccount(session.email, { displayName: session.displayName || fallbackName });
    return session;
  }

  async function login({ email, password, role, remember = true }) {
    const data = await api.post('/server/auth_handler/login', {
      email,
      password,
      role: routeRole(role),
      deviceId: browserDeviceId(),
    });
    return finishLogin(data, remember);
  }

  async function verifyMfa({ challenge, code, remember = true }) {
    const data = await api.post('/server/auth_handler/mfa/verify', {
      challenge,
      code,
      deviceId: browserDeviceId(),
    });
    return finishLogin(data, remember);
  }

  async function loginWithProvider({
    provider,
    email,
    mode = 'auto',
    displayName = '',
    role = 'CLIENT',
    remember = true,
    handoff = '',
  }) {
    const data = await api.post('/server/auth_handler/social-session', {
      handoff: handoff || undefined,
      provider,
      email,
      displayName,
      role: routeRole(role),
      mode,
      deviceId: browserDeviceId(),
    });
    if (data?.mfaRequired) {
      return {
        mfaRequired: true,
        challenge: data.challenge,
        method: data.method,
        hint: data.hint || '',
        email: data.email || email,
        profileHints: data.profileHints || null,
        provider,
        displayName,
        remember,
      };
    }
    const session = persistSession(data.session, remember);
    markJoinedAccount(session.email, { displayName: session.displayName || displayName });
    const hints = {
      ...(data.profileHints || {}),
      displayName: data.profileHints?.displayName || session.displayName || displayName,
      email: session.email,
      source: provider || data.profileHints?.source || 'social',
    };
    seedProfileFromProvider(session.email, hints);
    return session;
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  }

  function updateSessionProfile(patch) {
    if (!user) return null;
    const next = {
      ...user,
      displayName: patch.displayName !== undefined ? patch.displayName : user.displayName,
      licensedStates:
        patch.licensedStates !== undefined ? patch.licensedStates : user.licensedStates,
      phoneNumber: patch.phoneNumber !== undefined ? patch.phoneNumber : user.phoneNumber,
      avatarDataUrl:
        patch.avatarDataUrl !== undefined ? patch.avatarDataUrl : user.avatarDataUrl,
    };
    const saved = persistSession(next, true);
    markJoinedAccount(saved.email, {
      ...(patch.displayName !== undefined ? { displayName: saved.displayName } : {}),
      ...(patch.avatarDataUrl !== undefined ? { avatarDataUrl: saved.avatarDataUrl } : {}),
    });
    return saved;
  }

  function applyToken(token) {
    const session = { token, role: 'CLIENT', email: 'user', displayName: 'User' };
    return persistSession(session, true);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        verifyMfa,
        signup,
        loginWithProvider,
        logout,
        updateSessionProfile,
        applyToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
